import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import ReactTooltip from 'react-tooltip';
import CustomPropTypes from 'CustomPropTypes';
import { htmlDecode } from 'Utils';

export default function withStaticBuilder(WrappedComponent) {
  class StaticBuilderField extends React.Component {
    static delimiterSeparatedToTitleCase(delimter, string) {
      return string.split(delimter).map(word => (
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )).join(' ');
    }

    static init(id, props) {
      ReactDOM.render(
        <StaticBuilderField {...props} />,
        document.getElementById(id),
      );
    }

    static onPopoverClick(e, isMobileOptimization = false) {
      e.preventDefault();

      if (isMobileOptimization) {
        const button = e.currentTarget;

        // This is a hack to reopen the tooltip when the user
        // clicks away from a focused text input on mobile
        setTimeout(() => {
          ReactTooltip.show(button);
        }, 250);
      }
    }

    static onFocus(e) {
      const formInputs = [...document.querySelectorAll('.controls')];
      // If the current focused field is not the first field, we want to scroll to it
      if (e.currentTarget !== formInputs[0] && !formInputs[0].contains(e.currentTarget)) {
        window.scrollTo(0, (window.scrollY + e.currentTarget.getBoundingClientRect().top) - 46);
      }
    }

    constructor(props) {
      super(props);

      this.state = {
        // react treats props as string literals, however since we encode them on save
        // we need to decode them on load
        value: htmlDecode(this.props.initialValue),
        selectedValues: Object.keys(
          this.props.initialSelectedValues,
        ).reduce((runningDecodedSelectedValues, key) => {
          const decodedSelectedValues = runningDecodedSelectedValues;
          decodedSelectedValues[htmlDecode(key)] = this.props.initialSelectedValues[key];
          return decodedSelectedValues;
        }, {}),
      };

      this.onChange = this.onChange.bind(this);
      this.getLabel = this.getLabel.bind(this);
    }

    onChange(e) {
      if (e.target.type === 'checkbox') {
        const updatedSelectedValues = this.state.selectedValues;
        updatedSelectedValues[e.target.value] = e.target.checked;
        this.setState({
          selectedValues: updatedSelectedValues,
        });
      } else {
        const { value } = e.target;
        this.props.setCurrentFieldValue(value);
        this.setState({
          value,
        });
      }
    }

    getLabel() {
      let label = this.props.label;
      // if the label is empty, let's use the id in title case
      // the first character of a label should always be capatalized
      // the last character of a label should be a colon

      // if we only have id = pets_allowed -> Pets Allowed:
      // if we have label = pets allowed -> Pets allowed:
      if (!label) {
        label = StaticBuilderField.delimiterSeparatedToTitleCase('_', this.props.id);
      }
      label = label.charAt(0).toUpperCase() + label.slice(1);

      if (label.endsWith(':')) {
        return label;
      }

      return `${label}:`;
    }

    render() {
      const label = this.getLabel();

      let infoNode = null;
      if (this.props.info) {
        infoNode = (
          <button
            className="field-info"
            data-tip
            data-for={`${this.props.id}-fieldInfoTooltip`}
            data-class="field-info-tooltip"
            data-delay-hide="50"
            onClick={(e) => { StaticBuilderField.onPopoverClick(e, false); }}
          >
            <i className="icon icon-info-sign" />
            <ReactTooltip
              id={`${this.props.id}-fieldInfoTooltip`}
              className="field-info-tooltip"
              place="bottom"
              type="light"
              effect="solid"
              border
            >
              <div className="popover-inner">
                <div className="popover-title" />
                <div
                  className="popover-content"
                  dangerouslySetInnerHTML={{ __html: `${DOMPurify.sanitize(this.props.info)}` }}
                />
              </div>
            </ReactTooltip>
          </button>
        );
      }

      return (
        <React.Fragment>
          <label
            className="control-label"
            htmlFor={`${this.props.id}`}
            dangerouslySetInnerHTML={{ __html: `${DOMPurify.sanitize(label)}` }}
          />
          <div
            className="controls"
          >
            <div
              className="wrapped-static-field"
              onFocus={undefined}
            >
              <WrappedComponent
                onChange={this.onChange}
                value={this.state.value}
                selectedValues={this.state.selectedValues}
                {...this.props}
              />
            </div>
            {infoNode}
          </div>
        </React.Fragment>
      );
    }
  }

  StaticBuilderField.defaultProps = {
    label: '',
    initialValue: '',
    initialSelectedValues: {},
    info: '',
    setCurrentFieldValue: () => {},
  };

  StaticBuilderField.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    initialValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    initialSelectedValues: CustomPropTypes.shapeWithBooleanValues.isRequired,
    info: PropTypes.string,
    setCurrentFieldValue: PropTypes.func,
  };

  return StaticBuilderField;
}



// WEBPACK FOOTER //
// ./src-react/StaticBuilder/hocs/StaticBuilderField/index.jsx